import { useMemo } from 'react';
import { showTooltip, hideTooltip } from './Tooltip';

export type SankeyLink = { source: string; target: string; value: number };

const STATUS_COLORS: Record<string, string> = {
  a_consulter: '#D48C3E',
  a_traiter: '#E07A5F',
  valide: '#8CB369',
  archive: '#A9A193',
};

export default function Sankey({
  links,
  width = 540,
  height = 260,
  onClickLink,
}: {
  links: SankeyLink[];
  width?: number;
  height?: number;
  onClickLink?: (l: SankeyLink) => void;
}) {
  const safeLinks: SankeyLink[] = (Array.isArray(links) ? links : [])
    .filter((l): l is SankeyLink => !!l && l.source != null && l.target != null)
    .map((l) => ({
      source: String(l.source),
      target: String(l.target),
      value: Number(l.value ?? 1),
    }));
  const { sources, targets, positioned } = useMemo(() => {
    const srcTotals = new Map<string, number>();
    const tgtTotals = new Map<string, number>();
    for (const l of safeLinks) {
      srcTotals.set(l.source, (srcTotals.get(l.source) ?? 0) + l.value);
      tgtTotals.set(l.target, (tgtTotals.get(l.target) ?? 0) + l.value);
    }
    const total = [...srcTotals.values()].reduce((a, b) => a + b, 0) || 1;
    const pad = 6;
    const sources = [...srcTotals.entries()];
    const targets = [...tgtTotals.entries()];

    const sHeight = height - pad * (sources.length + 1);
    const tHeight = height - pad * (targets.length + 1);
    let sY = pad;
    const srcPos = new Map<string, { y: number; h: number }>();
    for (const [k, v] of sources) {
      const h = (v / total) * sHeight;
      srcPos.set(k, { y: sY, h });
      sY += h + pad;
    }
    let tY = pad;
    const tgtPos = new Map<string, { y: number; h: number }>();
    for (const [k, v] of targets) {
      const h = (v / total) * tHeight;
      tgtPos.set(k, { y: tY, h });
      tY += h + pad;
    }

    // Pour chaque lien : on accumule la position Y au sein de la bande source/target
    const srcUsed = new Map<string, number>();
    const tgtUsed = new Map<string, number>();
    const positioned = safeLinks.map((l) => {
      const s = srcPos.get(l.source)!;
      const t = tgtPos.get(l.target)!;
      const frac = l.value / total;
      const bandH = frac * sHeight;
      const sUsed = srcUsed.get(l.source) ?? 0;
      const tUsed = tgtUsed.get(l.target) ?? 0;
      srcUsed.set(l.source, sUsed + bandH);
      tgtUsed.set(l.target, tUsed + (frac * tHeight));
      return {
        l,
        sy0: s.y + sUsed,
        sy1: s.y + sUsed + bandH,
        ty0: t.y + tUsed,
        ty1: t.y + tUsed + frac * tHeight,
      };
    });
    return { sources, targets, positioned };
  }, [safeLinks, height]);

  const nodeW = 14;
  const leftX = 0;
  const rightX = width - nodeW;
  const midA = width * 0.38;
  const midB = width * 0.62;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`}>
      {positioned.map((p, i) => {
        const d = `M ${leftX + nodeW} ${(p.sy0 + p.sy1) / 2}
          C ${midA} ${(p.sy0 + p.sy1) / 2}, ${midB} ${(p.ty0 + p.ty1) / 2}, ${rightX} ${(p.ty0 + p.ty1) / 2}`;
        const thickness = Math.max(1.5, p.sy1 - p.sy0);
        return (
          <path
            key={i}
            d={d}
            className="sankey-link"
            strokeWidth={thickness}
            stroke={STATUS_COLORS[p.l.target] ?? 'var(--c-sage)'}
            style={{ cursor: onClickLink ? 'pointer' : 'default' }}
            onClick={() => onClickLink?.(p.l)}
            onMouseMove={(e) =>
              showTooltip({
                x: e.clientX,
                y: e.clientY,
                title: `${p.l.source} → ${p.l.target}`,
                rows: [
                  {
                    label: 'Volume',
                    value: Number.isFinite(p.l.value)
                      ? Math.round(p.l.value * 10) / 10 + ''
                      : '—',
                  },
                ],
              })
            }
            onMouseLeave={hideTooltip}
          />
        );
      })}
      {sources.map(([k], i) => {
        const pos = positioned.find((p) => p.l.source === k);
        const y0 = pos ? pos.sy0 : 0;
        const h = sources.map(([kk, vv]) => (kk === k ? vv : 0)).reduce((a, b) => a + b, 0);
        const total = sources.reduce((a, [, v]) => a + v, 0) || 1;
        const bh = (h / total) * (height - 6 * (sources.length + 1));
        return (
          <g key={`s${i}`} transform={`translate(${leftX} ${y0})`}>
            <rect width={nodeW} height={bh} rx={3} fill="var(--c-text)" />
            <text x={nodeW + 5} y={bh / 2 + 4} fontSize={11} fill="var(--c-text)">
              {k}
            </text>
          </g>
        );
      })}
      {targets.map(([k], i) => {
        const pos = positioned.find((p) => p.l.target === k);
        const y0 = pos ? pos.ty0 : 0;
        const total = targets.reduce((a, [, v]) => a + v, 0) || 1;
        const v = targets.find(([kk]) => kk === k)![1];
        const bh = (v / total) * (height - 6 * (targets.length + 1));
        const color = STATUS_COLORS[k] ?? 'var(--c-sage)';
        return (
          <g key={`t${i}`} transform={`translate(${rightX} ${y0})`}>
            <rect width={nodeW} height={bh} rx={3} fill={color} />
            <text x={-5} y={bh / 2 + 4} textAnchor="end" fontSize={11} fill="var(--c-text)">
              {labelForStatus(k)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function labelForStatus(s: string) {
  switch (s) {
    case 'a_consulter':
      return 'À consulter';
    case 'a_traiter':
      return 'À revoir';
    case 'valide':
      return 'Validé';
    case 'archive':
      return 'Archivé';
    default:
      return s;
  }
}
